/**
 * The board-level filter row (dashboard design rules §4.3): ONE left-aligned
 * row above everything it scopes — never per-chart, never inside a card.
 * Date range first (preset chips + a custom `DateRangePicker`), then the
 * dimension `FilterSelect`s. All state lives in the URL search params via
 * `lib/drill`, so every panel below re-renders against the same slice, the
 * back button undoes a filter, and the view is shareable.
 *
 * URL contract:
 * - `range=today|7d|30d|90d` — preset window (default `30d` when absent, or
 *   whatever the board passed as `defaultPreset`)
 * - `from=YYYY-MM-DD&to=YYYY-MM-DD` — custom window (mutually exclusive with
 *   `range`; boards whose endpoint only serves "the last N days" turn this off)
 * - one comma-joined param per dimension, e.g. `sub=id1,id2&severity=Sev1`
 *
 * Panels read the same slice with `useBoardRange()` + `useDrillParams(...)`.
 */

import {
  Chip,
  ChipGroup,
  type DateRange,
  DateRangePicker,
  FilterSelect,
  type FilterSelectOption,
  parseDate,
} from "@norican/react";
import { useSearchParams } from "react-router";

import { useDrillParams } from "@/lib/drill";

/** The preset windows the rules name: today, 7/30/90 days. */
export const RANGE_PRESETS = [
  { id: "today", label: "Today", days: 1 },
  { id: "7d", label: "7 days", days: 7 },
  { id: "30d", label: "30 days", days: 30 },
  { id: "90d", label: "90 days", days: 90 },
] as const;

export type RangePresetId = (typeof RANGE_PRESETS)[number]["id"];

/** Preset applied when the URL carries neither `range` nor `from`/`to`. */
export const DEFAULT_RANGE_PRESET: RangePresetId = "30d";

export interface BoardRange {
  /** Active preset, or `null` when a custom `from`/`to` window is set. */
  preset: RangePresetId | null;
  /** Window size in days (custom windows are measured inclusively). */
  days: number;
  /** Inclusive start of the window, as a local-midnight `Date`. */
  fromDate: Date;
  /** Inclusive end of the window, as a local-midnight `Date`. */
  toDate: Date;
  /** ISO dates (`YYYY-MM-DD`) of the window bounds — for query params. */
  fromIso: string;
  toIso: string;
}

function toLocalMidnight(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function toIsoDay(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function parseIsoDay(value: string | undefined): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * The date window the board is scoped to, resolved from the URL (`range` or
 * `from`/`to`, falling back to `defaultPreset`). Use this in panels and query
 * hooks so their numbers agree with the `FilterRow` above them — and pass the
 * same `defaultPreset` the row was given, or the two will disagree.
 */
export function useBoardRange(defaultPreset: RangePresetId = DEFAULT_RANGE_PRESET): BoardRange {
  const [searchParams] = useSearchParams();
  const rangeParam = searchParams.get("range");
  const fromParam = parseIsoDay(searchParams.get("from") ?? undefined);
  const toParam = parseIsoDay(searchParams.get("to") ?? undefined);

  if (fromParam && toParam && !rangeParam) {
    const days = Math.round((toParam.getTime() - fromParam.getTime()) / 86_400_000) + 1;
    return {
      preset: null,
      days: Math.max(days, 1),
      fromDate: fromParam,
      toDate: toParam,
      fromIso: toIsoDay(fromParam),
      toIso: toIsoDay(toParam),
    };
  }

  const preset =
    RANGE_PRESETS.find((candidate) => candidate.id === rangeParam)?.id ?? defaultPreset;
  const days = RANGE_PRESETS.find((candidate) => candidate.id === preset)?.days ?? 30;
  const toDate = toLocalMidnight(new Date());
  const fromDate = new Date(toDate.getTime() - (days - 1) * 86_400_000);
  return {
    preset,
    days,
    fromDate,
    toDate,
    fromIso: toIsoDay(fromDate),
    toIso: toIsoDay(toDate),
  };
}

/** One `FilterSelect` dimension scoped by the row (subscription, severity, …). */
export interface FilterDimension {
  /** URL search param the selection is stored under (comma-joined). */
  param: string;
  /** Trigger placeholder and accessible label, e.g. "Subscription". */
  label: string;
  /** The selectable options (value = what lands in the URL). */
  options: FilterSelectOption[];
  /** Noun for the ≥2 summary, e.g. "subscription" → "3 subscriptions". */
  noun?: string;
}

export interface FilterRowProps {
  /** Dimension filters, rendered after the date range. */
  dimensions?: FilterDimension[];
  /** Hide the date-range controls for boards without a time axis. */
  showRange?: boolean;
  /** Which preset chips to offer (default: every {@link RANGE_PRESETS} entry). */
  presets?: readonly RangePresetId[];
  /**
   * Offer an arbitrary `from`/`to` window (default `true`). Pass `false` on a
   * board whose endpoint only serves "the last N days": it cannot honour a
   * window that ends in the past, and rendering one anyway would label the
   * wrong period with the picked dates.
   */
  allowCustomRange?: boolean;
  /** Preset applied when the URL carries no range (default `30d`). */
  defaultPreset?: RangePresetId;
  className?: string;
}

export function FilterRow({
  dimensions = [],
  showRange = true,
  presets,
  allowCustomRange = true,
  defaultPreset = DEFAULT_RANGE_PRESET,
  className,
}: FilterRowProps) {
  const dimensionParams = dimensions.map((dimension) => dimension.param);
  const drill = useDrillParams("range", "from", "to", ...dimensionParams);
  const range = useBoardRange(defaultPreset);
  const chips = presets
    ? RANGE_PRESETS.filter((preset) => presets.includes(preset.id))
    : RANGE_PRESETS;

  const customValue: DateRange | null = (() => {
    if (range.preset !== null) {
      return null;
    }
    try {
      return { from: parseDate(range.fromIso), to: parseDate(range.toIso) };
    } catch {
      return null;
    }
  })();

  return (
    <div className={`flex flex-wrap items-end justify-start gap-3 ${className ?? ""}`}>
      {showRange && (
        <>
          <ChipGroup.Root
            aria-label="Date range"
            value={range.preset ? [range.preset] : []}
            onValueChange={(value) => {
              const preset = value[0];
              if (preset) {
                drill.set({ range: preset, from: null, to: null });
              }
            }}
          >
            {chips.map((preset) => (
              <Chip key={preset.id} value={preset.id} size="sm">
                {preset.label}
              </Chip>
            ))}
          </ChipGroup.Root>
          {allowCustomRange && (
            <DateRangePicker
              size="sm"
              label="Custom range"
              value={customValue}
              onValueChange={(value) => {
                if (value) {
                  drill.set({
                    range: null,
                    from: value.from.toString(),
                    to: value.to.toString(),
                  });
                } else {
                  drill.set({ range: null, from: null, to: null });
                }
              }}
            />
          )}
        </>
      )}
      {dimensions.map((dimension) => (
        <FilterSelect
          key={dimension.param}
          size="sm"
          aria-label={dimension.label}
          placeholder={dimension.label}
          noun={dimension.noun}
          options={dimension.options}
          value={drill.getList(dimension.param)}
          onValueChange={(value) => drill.set({ [dimension.param]: value })}
        />
      ))}
    </div>
  );
}
